/**
 * LunchMoney API Client (Read-Only)
 * 
 * This API client provides read-only access to the LunchMoney API.
 * All methods are GET operations - no data modification capabilities.
 * 
 * For write operations (POST/PUT), see scripts/lunchMoneyWrite.ts
 * which is located outside the src/ directory and will NOT be included
 * in any production builds.
 */

import type { Category } from '../types/category';
import type { Asset } from '../types/asset';
import type { PlaidAccount } from '../types/plaidAccount';
import type { Transaction } from '../types/transaction';
import type { RecurringItem } from '../types/recurring';
import type { BudgetsResponse } from '../types/budget';

const API_BASE_URL = 'https://dev.lunchmoney.app/v1';

export class LunchMoneyAPI {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
      ...options,
    });

    if (!response.ok) {
      let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = `${errorMessage} - ${JSON.stringify(errorData.error)}`;
        }
      } catch {
        // If response isn't JSON, use the status text
      }
      
      // Provide more helpful error messages
      if (response.status === 401) {
        errorMessage = `Unauthorized (401): Please check your API key. The API key may be invalid or expired. ${errorMessage}`;
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getUser() {
    return this.request<{
      user_name: string;
      user_email: string;
      user_id: number;
      account_id: number;
      budget_name: string;
      primary_currency: string;
      api_key_label: string | null;
    }>('/me');
  }

  async getCategories(): Promise<Category[]> {
    const response = await this.request<{ categories: Category[] }>('/categories');
    return response.categories;
  }

  async getRecurringItems(startDate: string, endDate: string): Promise<RecurringItem[]> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    return this.request<RecurringItem[]>(`/recurring_items?${params.toString()}`);
  }

  async getAssets(): Promise<Asset[]> {
    const response = await this.request<{ assets: Asset[] }>('/assets');
    return response.assets || [];
  }

  async getPlaidAccounts(): Promise<PlaidAccount[]> {
    const response = await this.request<{ plaid_accounts: PlaidAccount[] }>('/plaid_accounts');
    return response.plaid_accounts || [];
  }

  async getTransactions(startDate: string, endDate: string, plaidAccountIds?: number[]): Promise<Transaction[]> {
    // If specific account IDs provided, fetch transactions for each account
    if (plaidAccountIds && plaidAccountIds.length > 0) {
      const allTransactions: Transaction[] = [];
      // Fetch transactions for each account (API doesn't support multiple plaid_account_id in one call)
      for (const accountId of plaidAccountIds) {
        const accountParams = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          plaid_account_id: accountId.toString(),
        });
        const response = await this.request<{ transactions: Transaction[] }>(`/transactions?${accountParams.toString()}`);
        if (response.transactions) {
          allTransactions.push(...response.transactions);
        }
      }
      return allTransactions;
    }
    
    // Otherwise fetch all transactions
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    const response = await this.request<{ transactions: Transaction[] }>(`/transactions?${params.toString()}`);
    return response.transactions || [];
  }

  async getBudgets(startDate: string, endDate: string, currency?: string): Promise<BudgetsResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (currency) {
      params.append('currency', currency);
    }
    return this.request<BudgetsResponse>(`/budgets?${params.toString()}`);
  }
}
